import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { Users, Mail, Plus, X, Save, Eye, ShieldCheck, Star, RefreshCw } from 'lucide-react';

const CM_DOMAIN = '@cmidiomas.com';

export default function RecipientsPanel({ api }) {
  const [types, setTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // key -> {mode,user_ids,extra_emails,exclude_user_ids}
  const [savingKey, setSavingKey] = useState(null);
  const [previews, setPreviews] = useState({}); // key -> {count,cm_count,recipients}
  const [previewLoading, setPreviewLoading] = useState(null);
  const [extraInputs, setExtraInputs] = useState({}); // key -> string

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, elig] = await Promise.all([
        api().get('/config/notification-recipients'),
        api().get('/config/notification-recipients/eligible-users'),
      ]);
      setTypes(cat.data.types || []);
      setUsers(elig.data.users || []);
      const d = {};
      (cat.data.types || []).forEach((t) => {
        d[t.key] = {
          mode: t.mode,
          user_ids: [...(t.user_ids || [])],
          extra_emails: [...(t.extra_emails || [])],
          exclude_user_ids: [...(t.exclude_user_ids || [])],
        };
      });
      setDrafts(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron cargar los destinatarios');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const update = (key, patch) =>
    setDrafts((s) => ({ ...s, [key]: { ...s[key], ...patch } }));

  const toggleInList = (key, field, id) => {
    const cur = drafts[key][field] || [];
    update(key, { [field]: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };

  const addExtra = (key) => {
    const val = (extraInputs[key] || '').trim();
    if (!val || !val.includes('@')) { toast.error('Correo inválido'); return; }
    if ((drafts[key].extra_emails || []).map((e) => e.toLowerCase()).includes(val.toLowerCase())) {
      toast.error('Ya está agregado'); return;
    }
    update(key, { extra_emails: [...(drafts[key].extra_emails || []), val] });
    setExtraInputs((s) => ({ ...s, [key]: '' }));
  };

  const removeExtra = (key, em) =>
    update(key, { extra_emails: (drafts[key].extra_emails || []).filter((e) => e !== em) });

  const save = async (key) => {
    setSavingKey(key);
    try {
      await api().put('/config/notification-recipients', { key, ...drafts[key] });
      toast.success('Destinatarios guardados');
      await preview(key);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSavingKey(null);
    }
  };

  const preview = async (key) => {
    setPreviewLoading(key);
    try {
      const res = await api().get(`/config/notification-recipients/preview?key=${key}`);
      setPreviews((s) => ({ ...s, [key]: res.data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo obtener la vista previa');
    } finally {
      setPreviewLoading(null);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground" data-testid="recipients-loading">Cargando destinatarios…</div>;
  }

  return (
    <div className="space-y-4" data-testid="recipients-panel">
      <Card>
        <CardContent className="pt-4">
          <p className="text-sm text-muted-foreground">
            Decide <b>quién recibe cada notificación</b>. Los correos <b>{CM_DOMAIN}</b> tienen
            prioridad (siempre van primero y no se pierden por duplicados de dominio). El filtro
            de hora/día personal <b>se ignora</b>: los destinatarios elegidos siempre reciben.
          </p>
        </CardContent>
      </Card>

      {types.map((t) => {
        const d = drafts[t.key] || {};
        const isCustom = d.mode === 'custom';
        const pv = previews[t.key];
        return (
          <Card key={t.key} data-testid={`recipients-card-${t.key}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle className="text-base flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" /> {t.label}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">{isCustom ? 'Lista personalizada' : 'Todos los admins'}</Label>
                  <Switch
                    checked={isCustom}
                    onCheckedChange={(v) => update(t.key, { mode: v ? 'custom' : 'all_admins' })}
                    data-testid={`recipients-mode-${t.key}`}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {isCustom ? (
                <div>
                  <Label className="text-xs flex items-center gap-1 mb-2">
                    <Users className="w-3.5 h-3.5" /> Usuarios que reciben
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-56 overflow-y-auto border rounded-md p-2">
                    {users.map((u) => (
                      <label
                        key={u.id}
                        className="flex items-center gap-2 text-sm py-1 px-1 rounded hover:bg-muted/50 cursor-pointer"
                      >
                        <Checkbox
                          checked={(d.user_ids || []).includes(u.id)}
                          onCheckedChange={() => toggleInList(t.key, 'user_ids', u.id)}
                          data-testid={`recipients-user-${t.key}-${u.id}`}
                        />
                        <span className="truncate">
                          {u.name}
                          {u.is_cm && <Star className="inline w-3 h-3 text-emerald-500 ml-1 mb-0.5" />}
                          <span className="text-muted-foreground"> · {u.email || 'sin correo'}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <Label className="text-xs flex items-center gap-1 mb-2">
                    <ShieldCheck className="w-3.5 h-3.5" /> Excluir admins específicos (opcional)
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-40 overflow-y-auto border rounded-md p-2">
                    {users.filter((u) => u.role === 'admin' || u.role === 'superadmin').map((u) => (
                      <label key={u.id} className="flex items-center gap-2 text-sm py-1 px-1 rounded hover:bg-muted/50 cursor-pointer">
                        <Checkbox
                          checked={(d.exclude_user_ids || []).includes(u.id)}
                          onCheckedChange={() => toggleInList(t.key, 'exclude_user_ids', u.id)}
                          data-testid={`recipients-exclude-${t.key}-${u.id}`}
                        />
                        <span className="truncate">
                          {u.name}
                          {u.is_cm && <Star className="inline w-3 h-3 text-emerald-500 ml-1 mb-0.5" />}
                          <span className="text-muted-foreground"> · {u.email || 'sin correo'}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Extra external emails */}
              <div>
                <Label className="text-xs mb-1 block">Correos externos adicionales</Label>
                <div className="flex gap-2">
                  <Input
                    value={extraInputs[t.key] || ''}
                    onChange={(e) => setExtraInputs((s) => ({ ...s, [t.key]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addExtra(t.key); } }}
                    placeholder="correo@externo.com"
                    className="h-9"
                    data-testid={`recipients-extra-input-${t.key}`}
                  />
                  <Button type="button" variant="outline" size="sm" onClick={() => addExtra(t.key)} data-testid={`recipients-add-extra-${t.key}`}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {(d.extra_emails || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {d.extra_emails.map((em) => (
                      <Badge key={em} variant="secondary" className="flex items-center gap-1">
                        {em}
                        <X className="w-3 h-3 cursor-pointer" onClick={() => removeExtra(t.key, em)} />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" onClick={() => save(t.key)} disabled={savingKey === t.key} data-testid={`recipients-save-${t.key}`}>
                  <Save className="w-4 h-4 mr-1" /> {savingKey === t.key ? 'Guardando…' : 'Guardar'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => preview(t.key)} disabled={previewLoading === t.key} data-testid={`recipients-preview-${t.key}`}>
                  <Eye className="w-4 h-4 mr-1" /> {previewLoading === t.key ? '…' : 'Ver quién recibirá'}
                </Button>
              </div>

              {pv && (
                <div className="border rounded-md p-3 bg-muted/30" data-testid={`recipients-preview-result-${t.key}`}>
                  <p className="text-xs font-medium mb-2">
                    {pv.count} destinatario(s) · {pv.cm_count} con {CM_DOMAIN}
                  </p>
                  {pv.count === 0 ? (
                    <p className="text-xs text-red-500">⚠️ Nadie recibirá esta notificación con la configuración actual.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {pv.recipients.map((r) => (
                        <Badge key={r.email} variant={r.is_cm ? 'default' : 'outline'} className="flex items-center gap-1">
                          {r.is_cm && <Star className="w-3 h-3" />}
                          {r.email}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      <div className="flex justify-end">
        <Button variant="ghost" size="sm" onClick={load} data-testid="recipients-reload">
          <RefreshCw className="w-4 h-4 mr-1" /> Recargar
        </Button>
      </div>
    </div>
  );
}
